import { execSync } from "child_process";
import path from "path";
import fs from "fs";

export class GitError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = "GitError";
  }
}

export interface GitEmailOptions {
  /** Time range in days to look back (default: 365) */
  timeRange?: number;
  /** Maximum number of commits to process (default: 1000) */
  maxCommits?: number;
  /** Email domains to exclude (default: ['noreply.github.com']) */
  excludeDomains?: string[];
  /** Whether to include the git config email as fallback (default: true) */
  includeConfigEmail?: boolean;
}

export interface GitEmail {
  email: string;
  isBot: boolean;
}

// Known bot domains and patterns
export const BOT_DOMAINS = [
  "noreply.github.com",
  "users.noreply.github.com",
  "renovate[bot]@users.noreply.github.com",
  "dependabot[bot]@users.noreply.github.com",
  "github-actions[bot]@users.noreply.github.com",
  "snyk-bot@snyk.io",
  "security@snyk.io",
  "bot@renovateapp.com",
  "release-bot@",
  "builds@",
  "automation@",
  "noreply@",
  "actions@github.com",
  "dependabot-preview[bot]@users.noreply.github.com",
  "greenkeeper[bot]@users.noreply.github.com",
  "github-actions@github.com",
  "notifications@github.com",
  "semantic-release-bot@",
  "azure-pipelines[bot]@",
  "github-actions[bot]@",
  "dependabot[bot]@",
  "renovate[bot]@",
  "circleci@",
  "jenkins@",
  "travis@",
  "gitlab@",
  "bot@",
  "[bot]@",
] as const;

// Bot name patterns that might appear in the email username part
const BOT_NAME_PATTERNS = [
  "dependabot",
  "renovate",
  "github-actions",
  "snyk-bot",
  "semantic-release",
  "greenkeeper",
  "azure-pipelines",
  "circleci",
  "jenkins",
  "travis",
  "gitlab",
] as const;

// Common prefixes that indicate a bot when combined with other patterns
const BOT_PREFIXES = [
  "bot-",
  "bot.",
  "ci-",
  "ci.",
  "auto-",
  "auto.",
  "build-",
  "build.",
  "test-",
  "automation-",
] as const;

// Pure Functions

export function isValidEmail(email: string): boolean {
  // RFC 5322 compliant email regex that also rejects consecutive dots
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+(?<!\.)@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.includes(".") && !email.includes("..");
}

export function isBotEmail(email: string): boolean {
  const lowerEmail = email.toLowerCase();

  // Check against known bot domains
  if (BOT_DOMAINS.some((domain) => lowerEmail.includes(domain))) {
    return true;
  }

  // Extract username part (before @)
  const username = lowerEmail.split("@")[0];

  // Check for bot patterns in username
  if (BOT_NAME_PATTERNS.some((pattern) => username.includes(pattern))) {
    return true;
  }

  // Check for bot prefixes
  if (BOT_PREFIXES.some((prefix) => username.startsWith(prefix))) {
    return true;
  }

  // Check for common bot patterns
  return (
    // Check for UUIDs or long hexadecimal strings (common in auto-generated emails)
    /[0-9a-f]{8}[-_][0-9a-f]{4}[-_][0-9a-f]{4}[-_][0-9a-f]{4}[-_][0-9a-f]{12}/.test(
      username,
    ) ||
    // Check for [bot] suffix
    /\[bot\]$/.test(username) ||
    // Check for exact bot username or starts with bot
    username === "bot" ||
    username.startsWith("bot.") ||
    // Check for numbered service accounts (e.g. service123456)
    /^(service|account|user|admin)\d{4,}$/.test(username) ||
    // Check for automation numbers
    /^automation-\d+$/.test(username)
  );
}

export function parseGitEmails(
  gitLog: string,
  excludeDomains: string[],
): Set<GitEmail> {
  const emailMap = new Map<string, GitEmail>();

  // Split the log into lines and process each line
  const lines = gitLog.split("\n");
  for (const line of lines) {
    // Try to extract email from <email> format first
    const bracketMatch = line.match(/<([^>]+)>/);
    // If no bracket format, treat the whole line as potential email
    const email = (bracketMatch ? bracketMatch[1] : line).toLowerCase().trim();

    if (
      isValidEmail(email) &&
      !excludeDomains.some((domain) => email.endsWith(domain))
    ) {
      emailMap.set(email, {
        email,
        isBot: isBotEmail(email),
      });
    }
  }

  return new Set(emailMap.values());
}

export function buildGitLogCommand(
  timeRange: number,
  maxCommits: number,
): string {
  const since = new Date();
  since.setDate(since.getDate() - timeRange);
  return `git log -n ${maxCommits} --since="${since.toISOString()}" --format="%aE%n%cE"`;
}

export function isGitDirectory(dirPath: string): boolean {
  return path.basename(dirPath) === ".git";
}

export function getParentDirectory(dirPath: string): string {
  return path.dirname(dirPath);
}

// I/O Functions

export function findGitRoot(startPath: string = process.cwd()): string | null {
  let currentPath = startPath;
  const rootPath = path.parse(currentPath).root;

  while (currentPath !== rootPath) {
    if (fs.existsSync(path.join(currentPath, ".git"))) {
      return currentPath;
    }
    currentPath = getParentDirectory(currentPath);
  }

  return null;
}

export function runGitCommand(command: string, gitRoot: string): string {
  try {
    return execSync(command, {
      cwd: gitRoot,
      encoding: "utf-8",
      stdio: ["pipe", "pipe", "pipe"],
      maxBuffer: 10 * 1024 * 1024, // 10MB buffer for large repos
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new GitError(
        `Git command failed: ${error.message}`,
        "COMMAND_FAILED",
      );
    }
    throw error;
  }
}

export async function getConfigEmail(gitRoot: string): Promise<string | null> {
  try {
    const email = runGitCommand("git config user.email", gitRoot).trim();
    return isValidEmail(email) ? email : null;
  } catch {
    return null;
  }
}

export async function getGitEmails(
  options: GitEmailOptions = {},
): Promise<GitEmail[]> {
  const {
    timeRange = 365,
    maxCommits = 1000,
    excludeDomains = ["noreply.github.com"],
    includeConfigEmail = true,
  } = options;

  const gitRoot = findGitRoot();
  if (!gitRoot) {
    throw new GitError(
      "Not a git repository (or any of the parent directories)",
      "NO_GIT_REPO",
    );
  }

  try {
    const gitLog = runGitCommand(
      buildGitLogCommand(timeRange, maxCommits),
      gitRoot,
    );
    const emailSet = parseGitEmails(gitLog, excludeDomains);

    if (emailSet.size === 0 && includeConfigEmail) {
      const configEmail = await getConfigEmail(gitRoot);
      if (configEmail) {
        emailSet.add({
          email: configEmail,
          isBot: isBotEmail(configEmail),
        });
      }
    }

    if (emailSet.size === 0) {
      throw new GitError(
        "No valid email addresses found in git history",
        "NO_EMAILS_FOUND",
      );
    }

    return Array.from(emailSet);
  } catch (error) {
    if (error instanceof GitError) {
      throw error;
    }
    throw new GitError(
      `Failed to get git emails: ${error instanceof Error ? error.message : "Unknown error"}`,
      "UNKNOWN_ERROR",
    );
  }
}
